/**
 * Email Automation Stats Page
 * Route: /marketing/automations/email/:id/stats
 *
 * Faithful replica of the PHP automation-stats.php — header with date range,
 * 7-card stat summary, Recharts area chart, expandable per-step analytics,
 * click tracking table, details grid, content modal, refresh contacts modal.
 */
import { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { CampaignDailyChart } from './CampaignDailyChart';
import { PauseAutomationDialog } from '@/components/marketing/PauseAutomationDialog';
import { MarketingHistoryCard } from '@/components/marketing/MarketingHistoryCard';
import { RemovedStepsSection, describeRemoval, type StepRemoval } from '@/components/marketing/RemovedStepsSection';
import { apiClient } from '@/services/apiClient';
import * as marketingSvc from '@/services/marketing.service';
import type { EmailAutomationContactCounts, EmailAutomationSendHold, RequestInfoLeadCounts } from '@/services/marketing.service';
import { DatePicker } from '@/components/ui/DatePicker';
import { useUserTimezone } from '@/hooks/useUserTimezone';
import { formatPacificDateTime } from '@/lib/facilityTimezone';
import { useToast } from '@/hooks/useToast';
import { statBox, contactsPath } from '@/components/marketing/statBox';
import { cn } from '@/lib/utils';

// ─── Types ───────────────────────────────────────────────────────────────────

/** Hold fields are absent from older API builds, which reads as "not held". */
interface StatsAutomation extends Partial<EmailAutomationSendHold> {
  automationId: number;
  name: string;
  description: string | null;
  status: string;
  channel: string;
  entryListId: number | null;
  entryListName: string;
  exitCriteria: number;
  allowReentry: boolean;
  unsubscribeGroup: string;
  categories: string;
  createdAt: string;
}

interface StatsSends {
  /** Sends SendGrid accepted — failed and held tries are not counted. */
  totalSent: number;
  /** Failed tries, one per try: a retried step adds one each time. */
  failedAttempts: number;
  delivered: number;
  opens: number;
  clicks: number;
  replies: number;
  bounces: number;
  unsubscribes: number;
  spamReports: number;
}

interface DailyStat {
  date: string;
  opens: number;
  clicks: number;
}

interface StepClickItem {
  url: string;
  clicks: number;
}

interface StepStats {
  stepId: number;
  stepOrder: number;
  subject: string;
  preheader: string;
  senderEmail: string;
  senderName: string;
  delayMinutes: number;
  htmlContent: string;
  /** Accepted sends only — the denominator of every rate on the card. */
  sent: number;
  /** Failed tries on this step. Absent from older API builds. */
  failedAttempts?: number;
  delivered: number;
  opens: number;
  clicks: number;
  replies: number;
  bounces: number;
  clickTracking?: StepClickItem[];
  /** Request-info leads credited to this step. Absent from older API builds. */
  leads?: RequestInfoLeadCounts;
}

type ContactCounts = EmailAutomationContactCounts;

type RemovedStepStats = StepStats & StepRemoval;

interface StatsResponse {
  automation: StatsAutomation;
  sends: StatsSends;
  contactCounts: ContactCounts;
  dailyStats: DailyStat[];
  steps: StepStats[];
  /** Steps taken out of the automation that still have sends. Absent from older API builds. */
  removedSteps?: RemovedStepStats[];
  clickTracking: StepClickItem[];
  /** Request-info leads across the automation. Absent from older API builds. */
  leads?: RequestInfoLeadCounts;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmtNum(n: number | null | undefined): string {
  if (n == null) return '0';
  return n.toLocaleString();
}

function formatCreatedDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${days[d.getDay()]}, ${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

function formatDelayText(minutes: number, isFirst = false): string {
  if (minutes === 0) return isFirst ? 'Immediately' : 'Immediately after previous';
  if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  if (minutes < 1440) {
    const h = Math.round(minutes / 60 * 10) / 10;
    return `${h} hour${h !== 1 ? 's' : ''}`;
  }
  const d = Math.round(minutes / 1440 * 10) / 10;
  return `${d} day${d !== 1 ? 's' : ''}`;
}

const EXIT_CRITERIA_LABELS: Record<number, string> = {
  0: 'Contacts have received all emails in the automation',
  1: 'Contacts are removed from the list',
};

/** The lead tile reads the top-level `leads` counts, not `sends`. */
type StatCardKey = keyof StatsSends | 'leads';

/** A box's filters on the contacts page: what it counts, as the list filters it. */
type ContactsLink = Record<string, string>;

const STAT_CARDS: {
  key: StatCardKey;
  label: string;
  color: string;
  /** The contacts behind the number: every box opens them. */
  link: ContactsLink;
}[] = [
  // Emails Sent counts only sends SendGrid accepted. Failed tries are their own
  // figure, labelled as on the SMS stats page: attempts, not people — a retried
  // step adds one per try.
  { key: 'totalSent', label: 'Emails Sent', color: '#00aff0', link: { outcome: 'sent' } },
  { key: 'failedAttempts', label: 'Failed Attempts', color: '#dc3545', link: { outcome: 'failed' } },
  { key: 'delivered', label: 'Delivered', color: '#8e44ad', link: { outcome: 'delivered' } },
  { key: 'opens', label: 'Unique Opens', color: '#28a745', link: { outcome: 'opened' } },
  { key: 'clicks', label: 'Unique Clicks', color: '#ffc107', link: { outcome: 'clicked' } },
  { key: 'replies', label: 'Replies', color: '#17a2b8', link: { outcome: 'replied' } },
  { key: 'bounces', label: 'Bounces', color: '#dc3545', link: { outcome: 'bounced' } },
  { key: 'unsubscribes', label: 'Unsubscribes', color: '#dc3545', link: { outcome: 'unsubscribed' } },
  { key: 'spamReports', label: 'Spam Reports', color: '#dc3545', link: { outcome: 'spam' } },
  // Request-info leads: the automation's own contacts and whoever they
  // forwarded the link to, together; opens "Created a lead" (either kind).
  { key: 'leads', label: 'Leads', color: '#fd7e14', link: { lead: '1' } },
];

/** One box in a step card's grid. */
interface StepBox {
  label: string;
  value: number;
  color: string;
  rate?: number;
  link: ContactsLink;
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function EmailAutomationStatsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const userTz = useUserTimezone();
  const automationId = Number(id);

  // Every box on this page opens the contacts behind its number.
  const openContacts = (params: Record<string, string | number | undefined>) =>
    navigate(contactsPath(`/marketing/automations/email/${automationId}/contacts`, params));

  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().slice(0, 10));

  const [expandedSteps, setExpandedSteps] = useState<Set<number>>(new Set());
  const [contentModalOpen, setContentModalOpen] = useState(false);
  const [contentModalStep, setContentModalStep] = useState<{ subject: string; html: string } | null>(null);
  const [pauseConfirmOpen, setPauseConfirmOpen] = useState(false);
  const [refreshModalOpen, setRefreshModalOpen] = useState(false);
  const [refreshResult, setRefreshResult] = useState<{ added?: number; removed?: number; skipped?: number; total_contacts?: number; error?: string } | null>(null);
  const [loadingOverlay, setLoadingOverlay] = useState('');

  // Single API call to get all stats data (mirrors EmailCampaignStatsPage pattern)
  const { data, isLoading } = useQuery({
    queryKey: ['automation-stats-page', automationId, dateFrom, dateTo],
    queryFn: async (): Promise<StatsResponse> => {
      const res = await apiClient.get(`/marketing/automations/${automationId}/stats`, {
        params: { date_from: dateFrom, date_to: dateTo },
      });
      return res.data.data ?? res.data;
    },
    enabled: automationId > 0,
  });

  const automation = data?.automation;
  const heldSteps = automation?.sendHoldSteps ?? [];
  const sends = data?.sends ?? { totalSent: 0, failedAttempts: 0, delivered: 0, opens: 0, clicks: 0, replies: 0, bounces: 0, unsubscribes: 0, spamReports: 0 };
  const contacts = data?.contactCounts ?? { active: 0, completed: 0, removed: 0, unsubscribed: 0, invalid: 0, failed: 0, total: 0 };
  const dailyStats = data?.dailyStats ?? [];
  const stepsData = data?.steps ?? [];
  const removedSteps = data?.removedSteps ?? [];
  const clickTracking = data?.clickTracking ?? [];
  const totalClicks = useMemo(() => clickTracking.reduce((sum, ct) => sum + ct.clicks, 0), [clickTracking]);
  // Missing `leads` (an API that predates request-info links) reads as zeros.
  const leads = marketingSvc.leadCountsOf(data);
  const cardValues: Record<StatCardKey, number> = {
    ...sends,
    leads: leads.recipient + leads.referral,
  };

  // Redirect draft to editor
  useEffect(() => {
    if (automation && automation.status === 'draft') {
      navigate(`/marketing/automations/email/${automationId}`, { replace: true });
    }
  }, [automation, automationId, navigate]);

  const isActive = automation?.status === 'active';
  const isPaused = automation?.status === 'paused';

  // ── Mutations ──
  const pauseMut = useMutation({
    mutationFn: () => marketingSvc.pauseAutomation(automationId),
    onSuccess: () => {
      setLoadingOverlay('');
      qc.invalidateQueries({ queryKey: ['automation-stats-page', automationId] });
      qc.invalidateQueries({ queryKey: ['marketing-history'] });
    },
    onError: () => { setLoadingOverlay(''); },
  });

  const resumeMut = useMutation({
    mutationFn: () => marketingSvc.activateAutomation(automationId),
    onSuccess: () => {
      setLoadingOverlay('');
      qc.invalidateQueries({ queryKey: ['automation-stats-page', automationId] });
      qc.invalidateQueries({ queryKey: ['marketing-history'] });
    },
    onError: (err: any) => { setLoadingOverlay(''); alert(err?.message || 'Failed to resume.'); },
  });

  const syncMut = useMutation({
    mutationFn: () => marketingSvc.syncAutomationContacts(automationId),
    onSuccess: (result) => {
      setLoadingOverlay('');
      setRefreshResult({ added: result.added, removed: result.removed, skipped: result.skipped, total_contacts: contacts.total + result.added - result.removed });
      setRefreshModalOpen(true);
      qc.invalidateQueries({ queryKey: ['automation-stats-page', automationId] });
    },
    onError: () => {
      setLoadingOverlay('');
      setRefreshResult({ error: 'Network error — please try again.' });
      setRefreshModalOpen(true);
    },
  });

  const retryHoldMut = useMutation({
    mutationFn: () => marketingSvc.retryEmailAutomationSendHold(automationId),
    onSuccess: () => {
      toast.success('Trying again', 'The next try runs within 15 minutes.');
      qc.invalidateQueries({ queryKey: ['automation-stats-page', automationId] });
    },
    onError: () => {
      toast.error(
        'Could not restart sending',
        'Nothing was changed. Reload the page and press Try again now once more; if it keeps failing, ask your administrator.',
      );
    },
  });

  useEffect(() => {
    document.body.style.overflow = (contentModalOpen || refreshModalOpen) ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [contentModalOpen, refreshModalOpen]);

  function toggleStep(stepId: number) {
    setExpandedSteps(prev => {
      const next = new Set(prev);
      if (next.has(stepId)) next.delete(stepId);
      else next.add(stepId);
      return next;
    });
  }

  function viewStepContent(step: { subject?: string; htmlContent?: string }) {
    if (!step.htmlContent) return;
    setContentModalStep({ subject: step.subject || '(No subject)', html: step.htmlContent });
    setContentModalOpen(true);
  }

  // The dialog owns the confirm button's spinner, so no page overlay here.
  // Errors are swallowed so the mutation's onError handles them and the promise
  // never rejects out of the dialog.
  async function confirmPause() {
    try {
      await pauseMut.mutateAsync();
    } catch {
      /* surfaced by pauseMut.onError */
    }
  }

  function handleResume() {
    setLoadingOverlay('Resuming automation...');
    resumeMut.mutate();
  }

  function handleRefresh() {
    setLoadingOverlay('Refreshing contacts...');
    syncMut.mutate();
  }

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!automation) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 20px', color: '#999' }}>
        <p style={{ fontSize: 18 }}>Automation not found.</p>
        <button type="button" onClick={() => navigate('/marketing/automations')} style={{ marginTop: 16, color: '#00aff0', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14 }}>
          Back to Automations
        </button>
      </div>
    );
  }

  const statusClass = automation.status === 'active' ? 'eas-badge-active' : automation.status === 'paused' ? 'eas-badge-paused' : 'eas-badge-stopped';

  /**
   * One step's card. A removed step renders the same card with its old
   * position in a greyed badge and a line saying when it was removed.
   */
  function renderStepCard(step: StepStats, isFirst: boolean, removal?: StepRemoval) {
    const stepSent = step.sent ?? 0;
    const stepFailed = step.failedAttempts ?? 0;
    const stepDelivered = step.delivered ?? 0;
    const stepOpens = step.opens ?? 0;
    const stepClicks = step.clicks ?? 0;
    const stepReplies = step.replies ?? 0;
    const stepBounces = step.bounces ?? 0;
    const openRate = stepSent > 0 ? Math.round((stepOpens / stepSent) * 1000) / 10 : 0;
    const clickRate = stepSent > 0 ? Math.round((stepClicks / stepSent) * 1000) / 10 : 0;
    const deliveredRate = stepSent > 0 ? Math.round((stepDelivered / stepSent) * 1000) / 10 : 0;
    const replyRate = stepSent > 0 ? Math.round((stepReplies / stepSent) * 1000) / 10 : 0;
    const bounceRate = stepSent > 0 ? Math.round((stepBounces / stepSent) * 1000) / 10 : 0;
    const isExpanded = expandedSteps.has(step.stepId);
    const stepClickTracking = step.clickTracking ?? [];
    // Active steps always show their lead counts (zeros when the API predates
    // them). A removed step shows them only when the API reported them, so an
    // absent figure is never passed off as a real zero.
    const showStepLeads = !removal || step.leads != null;
    const stepLeads = marketingSvc.leadCountsOf(step);
    // One figure for the step: its own leads and referrals together, opening
    // the contacts with "Created a lead" (either kind) from this step.
    const stepLeadTotal = stepLeads.recipient + stepLeads.referral;
    const stepBoxes: StepBox[] = [
      { label: 'Sent', value: stepSent, color: '#00aff0', link: { outcome: 'sent' } },
      { label: 'Failed Attempts', value: stepFailed, color: stepFailed > 0 ? '#dc3545' : '#999', link: { outcome: 'failed' } },
      { label: 'Delivered', value: stepDelivered, color: '#8e44ad', rate: deliveredRate, link: { outcome: 'delivered' } },
      { label: 'Opened', value: stepOpens, color: '#28a745', rate: openRate, link: { outcome: 'opened' } },
      { label: 'Clicked', value: stepClicks, color: '#ffc107', rate: clickRate, link: { outcome: 'clicked' } },
      { label: 'Replied', value: stepReplies, color: stepReplies > 0 ? '#17a2b8' : '#999', rate: replyRate, link: { outcome: 'replied' } },
      { label: 'Bounced', value: stepBounces, color: stepBounces > 0 ? '#dc3545' : '#999', rate: bounceRate, link: { outcome: 'bounced' } },
      ...(showStepLeads ? [
        { label: 'Leads', value: stepLeadTotal, color: stepLeadTotal > 0 ? '#fd7e14' : '#999', link: { lead: '1' } },
      ] : []),
    ];

    const senderDisplay = step.senderName
      ? (step.senderEmail ? `${step.senderName} <${step.senderEmail}>` : step.senderName)
      : (step.senderEmail || 'No sender');

    return (
      <div key={step.stepId} className="eas-step-card">
        <div className="eas-step-header" onClick={() => toggleStep(step.stepId)}>
          <div className="eas-step-header-left">
            <div
              className="eas-step-number"
              style={removal ? { background: '#adb5bd' } : undefined}
              title={removal ? `Removed — was step ${removal.previousStepOrder}` : undefined}
            >
              {removal ? removal.previousStepOrder : step.stepOrder}
            </div>
            <div className="eas-step-info">
              <h4>{step.subject || '(No subject)'}</h4>
              {step.preheader && <div className="preheader">{step.preheader}</div>}
              <span className="delay-sender">{formatDelayText(step.delayMinutes, isFirst)} &middot; {senderDisplay}</span>
              {removal && <div className="removed-note">{describeRemoval(removal, userTz)}</div>}
            </div>
          </div>
          <div className="eas-step-mini">
            <div className="eas-step-mini-item">
              <div className="mini-value" style={{ color: '#00aff0' }}>{fmtNum(stepSent)}</div>
              <div className="mini-label">Sent</div>
            </div>
            <div className="eas-step-mini-item">
              <div className="mini-value" style={{ color: stepFailed > 0 ? '#dc3545' : '#999' }}>{fmtNum(stepFailed)}</div>
              <div className="mini-label">Failed Attempts</div>
            </div>
            <div className="eas-step-mini-item">
              <div className="mini-value" style={{ color: '#28a745' }}>{openRate}%</div>
              <div className="mini-label">Open Rate</div>
            </div>
            <div className="eas-step-mini-item">
              <div className="mini-value" style={{ color: '#ffc107' }}>{clickRate}%</div>
              <div className="mini-label">Click Rate</div>
            </div>
            {showStepLeads && (
              <div className="eas-step-mini-item">
                <div className="mini-value" style={{ color: stepLeadTotal > 0 ? '#fd7e14' : '#999' }}>
                  {fmtNum(stepLeadTotal)}
                </div>
                <div className="mini-label">Leads</div>
              </div>
            )}
          </div>
        </div>

        {isExpanded && (
          <div className="eas-step-body">
            {/* Every box opens the contacts behind it: this step, that outcome
                or lead. Counts are tries, not people — a retried step adds one
                per try. */}
            <div className="eas-step-detail-grid">
              {stepBoxes.map((stat) => {
                const box = statBox(stat.value > 0 ? () => openContacts({ stepId: step.stepId, ...stat.link }) : null);
                return (
                  <div key={stat.label} className={cn('eas-step-detail-stat', box.className)} {...box.props}>
                    <div className="sd-value" style={{ color: stat.color }}>{fmtNum(stat.value)}</div>
                    <div className="sd-label">{stat.label}</div>
                    {stat.rate != null && <div className="sd-rate">{stat.rate}%</div>}
                  </div>
                );
              })}
            </div>

            {/* Per-step click tracking */}
            {stepClickTracking.length > 0 && (
              <table className="eas-step-click-table">
                <thead>
                  <tr>
                    <th>URL</th>
                    <th>Clicks</th>
                  </tr>
                </thead>
                <tbody>
                  {stepClickTracking.map((link, i) => (
                    <tr key={i}>
                      <td><a href={link.url} target="_blank" rel="noopener noreferrer" className="eas-link-url">{link.url}</a></td>
                      <td>{fmtNum(link.clicks)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {step.htmlContent && (
              <div style={{ marginTop: 12, textAlign: 'right' }}>
                <button type="button" className="eas-action-btn" onClick={() => viewStepContent(step)} style={{ fontSize: 12, padding: '5px 12px' }}>
                  View Email Content
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <style>{`
        .eas-page { width: 100%; padding: 20px; margin: 0 auto; }

        .eas-channel-tag {
          display: inline-flex; align-items: center; padding: 3px 10px;
          border-radius: 4px; font-size: 13px; font-weight: 600; margin-left: 10px;
          vertical-align: middle; text-transform: capitalize;
        }
        .eas-channel-email { background: #e7f3ff; color: #00aff0; }
        .eas-channel-sms { background: #e8f5e9; color: #2e7d32; }

        .eas-header {
          display: flex; justify-content: space-between; align-items: flex-start;
          margin-bottom: 20px; padding-bottom: 15px; border-bottom: 1px solid #e5e5e5;
          /* Pinned to the very top: the negative margin swallows the page's 20px
             top padding so the header starts flush at scroll 0 and never travels;
             opaque so content slides underneath. */
          position: sticky; top: 0; z-index: 100; background: #fff;
          margin-top: -20px; padding-top: 20px;
        }
        .eas-header h1 { margin: 0 0 8px 0; font-size: 24px; font-weight: 600; color: #333; }
        .eas-meta { display: flex; align-items: center; gap: 15px; font-size: 14px; color: #666; }

        .eas-badge {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 4px 10px; border-radius: 12px; font-size: 12px;
          font-weight: 600; text-transform: capitalize;
        }
        .eas-badge::before {
          content: ''; width: 8px; height: 8px; border-radius: 50%; background: currentColor;
        }
        .eas-badge-active { background: #d4edda; color: #155724; }
        .eas-badge-paused { background: #fff3cd; color: #856404; }
        .eas-badge-stopped { background: #e9ecef; color: #666; }

        .eas-header-right {
          display: flex; flex-direction: column; align-items: flex-end; gap: 12px;
        }
        .eas-date-form { display: flex; gap: 10px; align-items: center; font-size: 13px; color: #666; }
        .eas-date-input {
          padding: 4px 8px; border: 1px solid #ddd; border-radius: 5px;
          font-size: 13px; background: white;
        }
        .eas-action-btn {
          padding: 6px 14px; font-size: 13px; font-weight: 600;
          color: #333; background: white; border: 1px solid #ddd;
          border-radius: 5px; cursor: pointer; transition: background 0.2s;
          display: inline-flex; align-items: center; gap: 5px;
        }
        .eas-action-btn:hover { background: #f5f5f5; }
        .eas-action-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .eas-action-btn.primary { background: #00aff0; color: white; border-color: #00aff0; }
        .eas-action-btn.primary:hover { background: #009dd8; }
        .eas-action-btn.danger { color: #dc3545; border-color: #dc3545; }
        .eas-action-btn.danger:hover { background: #fff5f5; }

        .eas-contacts-link {
          display: inline-flex; align-items: center; gap: 8px; text-decoration: none;
          cursor: pointer; padding: 8px 14px; border-radius: 5px; transition: background 0.2s;
        }
        .eas-contacts-link:hover { background: #e0f0ff; }

        /* Held steps: each is trying one contact an hour until a send on it
           goes through. */
        .eas-hold-banner {
          border: 1px solid #f5d0a9; border-left: 4px solid #e8590c; border-radius: 8px;
          background: #fff8f0; padding: 14px 18px; margin-bottom: 20px;
        }
        .eas-hold-head { display: flex; flex-wrap: wrap; align-items: flex-start; justify-content: space-between; gap: 8px 16px; margin-bottom: 4px; }
        .eas-hold-title { font-size: 15px; font-weight: 600; color: #8a3a04; }
        .eas-hold-step + .eas-hold-step { margin-top: 4px; }
        .eas-hold-text { font-size: 13px; color: #6b4a2b; line-height: 1.55; }
        .eas-hold-message {
          margin-top: 10px; padding: 8px 10px; border-radius: 5px; background: #fff;
          border: 1px solid #f5d0a9; font-size: 13px; color: #721c24; word-break: break-word;
        }
        .eas-hold-message-label {
          display: block; font-size: 11px; font-weight: 600; text-transform: uppercase;
          color: #8a3a04; margin-bottom: 2px;
        }
        .eas-hold-times { display: flex; flex-wrap: wrap; gap: 6px 20px; margin-top: 10px; font-size: 12px; color: #6b4a2b; }
        .eas-hold-times strong { color: #333; font-weight: 600; }

        .eas-summary {
          /* 10 cards: one row when wide, then 5x2 and 2x5. Keep the count
             and the breakpoints in step. */
          display: grid; grid-template-columns: repeat(10, 1fr); gap: 0;
          background: white; border: 1px solid #e5e5e5; border-radius: 8px;
          margin-bottom: 25px; overflow: hidden;
        }
        .eas-stat-card {
          padding: 20px 15px; text-align: center; border-right: 1px solid #e5e5e5;
        }
        .eas-stat-card:last-child { border-right: none; }
        .eas-stat-value { font-size: 28px; font-weight: 600; margin-bottom: 6px; }
        .eas-stat-value.zero { color: #999; }
        .eas-stat-label {
          font-size: 11px; font-weight: 600; text-transform: uppercase;
          color: #666; letter-spacing: 0.5px;
        }

        .eas-chart-container {
          background: white; border: 1px solid #e5e5e5; border-radius: 8px;
          padding: 20px; margin-bottom: 25px;
        }

        .eas-section {
          background: white; border: 1px solid #e5e5e5; border-radius: 8px;
          margin-bottom: 25px; overflow: hidden;
        }
        .eas-section-header {
          display: flex; align-items: center; gap: 8px;
          padding: 15px 20px; border-bottom: 1px solid #e5e5e5;
          font-weight: 600; color: #333;
        }
        .eas-info-icon {
          width: 16px; height: 16px; border-radius: 50%; background: #e5e5e5;
          color: #666; font-size: 11px; display: flex; align-items: center;
          justify-content: center; cursor: help;
        }

        .eas-step-card {
          background: #fff; border: 1px solid #e5e5e5; border-radius: 8px;
          margin-bottom: 12px; overflow: hidden;
        }
        .eas-step-header {
          display: flex; justify-content: space-between; align-items: center;
          padding: 16px 20px; cursor: pointer; transition: background 0.15s;
        }
        .eas-step-header:hover { background: #fafbfc; }
        .eas-step-header-left { display: flex; align-items: center; gap: 14px; }
        .eas-step-number {
          width: 32px; height: 32px; border-radius: 50%; background: #00aff0;
          color: white; display: flex; align-items: center; justify-content: center;
          font-size: 14px; font-weight: 700; flex-shrink: 0;
        }
        .eas-step-info h4 { margin: 0 0 3px 0; font-size: 14px; font-weight: 600; color: #333; }
        .eas-step-info .preheader { font-size: 12px; color: #999; margin-top: 2px; font-style: italic; }
        .eas-step-info .delay-sender { font-size: 12px; color: #999; }
        .eas-step-info .removed-note { font-size: 12px; color: #a3410a; margin-top: 2px; }
        .eas-step-mini { display: flex; flex-wrap: wrap; justify-content: flex-end; gap: 16px; }
        .eas-step-mini-item { text-align: center; }
        .eas-step-mini-item .mini-value { font-size: 18px; font-weight: 700; }
        .eas-step-mini-item .mini-label { font-size: 10px; font-weight: 600; text-transform: uppercase; color: #999; }

        .eas-step-body { padding: 0 20px 16px; border-top: 1px solid #f0f0f0; }
        .eas-step-detail-grid {
          /* 7 stats per step, 8 with its leads: one row when wide, then 4 and
             2 columns. */
          display: grid; grid-auto-flow: column; grid-auto-columns: 1fr; gap: 0;
          margin-top: 14px; border: 1px solid #e5e5e5; border-radius: 6px; overflow: hidden;
        }
        .eas-step-detail-stat {
          padding: 14px 10px; text-align: center; border-right: 1px solid #e5e5e5;
        }
        .eas-step-detail-stat:last-child { border-right: none; }
        .eas-step-detail-stat .sd-value { font-size: 22px; font-weight: 700; margin-bottom: 3px; }
        .eas-step-detail-stat .sd-label { font-size: 10px; font-weight: 600; text-transform: uppercase; color: #888; }
        .eas-step-detail-stat .sd-rate { font-size: 11px; color: #aaa; margin-top: 2px; }

        .eas-step-click-table { width: 100%; border-collapse: collapse; margin-top: 12px; }
        .eas-step-click-table th {
          text-align: left; padding: 8px 12px; font-size: 11px; font-weight: 600;
          text-transform: uppercase; color: #666; background: #f8f9fa; border-bottom: 1px solid #e5e5e5;
        }
        .eas-step-click-table th:last-child, .eas-step-click-table td:last-child { text-align: right; }
        .eas-step-click-table td { padding: 8px 12px; border-bottom: 1px solid #f0f0f0; font-size: 13px; color: #333; }
        .eas-step-click-table tr:last-child td { border-bottom: none; }

        .eas-table { width: 100%; border-collapse: collapse; }
        .eas-table th {
          text-align: left; padding: 12px 20px; font-size: 11px; font-weight: 600;
          text-transform: uppercase; color: #666; background: #f8f9fa;
          border-bottom: 1px solid #e5e5e5;
        }
        .eas-table th:last-child, .eas-table td:last-child { text-align: right; }
        .eas-table td { padding: 15px 20px; border-bottom: 1px solid #f0f0f0; font-size: 14px; color: #333; }
        .eas-table tr:last-child td { border-bottom: none; }

        .eas-rank-badge {
          display: inline-flex; align-items: center; justify-content: center;
          width: 24px; height: 24px; border-radius: 4px; font-size: 12px; font-weight: 600;
        }
        .eas-rank-1 { background: #ffd700; color: #333; }
        .eas-rank-2 { background: #c0c0c0; color: #333; }
        .eas-rank-3 { background: #cd7f32; color: white; }
        .eas-rank-default { background: #e9ecef; color: #666; }

        .eas-link-url { color: #00aff0; text-decoration: none; word-break: break-all; }
        .eas-link-url:hover { text-decoration: underline; }

        .eas-table-footer {
          display: flex; justify-content: space-between; padding: 12px 20px;
          background: #f8f9fa; font-size: 13px; color: #666;
        }
        .eas-table-footer strong { color: #333; }

        .eas-details-grid { display: grid; grid-template-columns: 200px 1fr; gap: 0; }
        .eas-detail-label {
          padding: 15px 20px; font-weight: 600; color: #333;
          background: #f8f9fa; border-bottom: 1px solid #e5e5e5; font-size: 14px;
        }
        .eas-detail-value {
          padding: 15px 20px; color: #666; border-bottom: 1px solid #e5e5e5; font-size: 14px;
        }
        .eas-detail-row:last-child .eas-detail-label,
        .eas-detail-row:last-child .eas-detail-value { border-bottom: none; }
        .eas-detail-value a { color: #00aff0; text-decoration: none; }
        .eas-detail-value a:hover { text-decoration: underline; }

        .eas-empty-state { padding: 40px 20px; text-align: center; color: #999; }

        .eas-modal-overlay {
          display: flex; position: fixed; inset: 0; background: rgba(0,0,0,0.5);
          z-index: 9999; justify-content: center; align-items: center;
        }
        .eas-modal {
          background: #fff; border-radius: 10px; width: 90%; max-width: 800px;
          max-height: 85vh; display: flex; flex-direction: column;
          box-shadow: 0 8px 30px rgba(0,0,0,0.25);
        }
        .eas-modal-header {
          display: flex; justify-content: space-between; align-items: center;
          padding: 16px 20px; border-bottom: 1px solid #e5e5e5;
        }
        .eas-modal-header h3 { margin: 0; font-size: 16px; font-weight: 600; }
        .eas-modal-close {
          background: none; border: none; font-size: 22px; cursor: pointer;
          color: #999; padding: 0 4px; line-height: 1;
        }
        .eas-modal-close:hover { color: #333; }
        .eas-modal-body { flex: 1; overflow: auto; padding: 0; }
        .eas-modal-body iframe { width: 100%; height: 500px; border: none; }

        .eas-refresh-overlay {
          display: flex; position: fixed; inset: 0; background: rgba(0,0,0,0.45);
          z-index: 100000; justify-content: center; align-items: center;
        }
        .eas-refresh-modal {
          background: #fff; border-radius: 10px; width: 400px; max-width: 90%;
          box-shadow: 0 8px 30px rgba(0,0,0,0.25); overflow: hidden;
          animation: easRefreshIn 0.25s ease;
        }
        @keyframes easRefreshIn { from { opacity: 0; transform: scale(0.92); } to { opacity: 1; transform: scale(1); } }
        .eas-refresh-header {
          display: flex; justify-content: space-between; align-items: center;
          padding: 16px 20px; border-bottom: 1px solid #e5e5e5;
        }
        .eas-refresh-header h3 { margin: 0; font-size: 16px; font-weight: 600; color: #333; }
        .eas-refresh-body { padding: 24px 20px; text-align: center; }
        .eas-refresh-icon { font-size: 40px; margin-bottom: 12px; }
        .eas-refresh-message { font-size: 15px; color: #333; margin-bottom: 6px; font-weight: 600; }
        .eas-refresh-details { font-size: 13px; color: #666; line-height: 1.6; }
        .eas-refresh-stat {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 4px 12px; border-radius: 16px; font-size: 13px; font-weight: 600; margin: 4px 4px;
        }
        .eas-refresh-stat.added { background: #d4edda; color: #155724; }
        .eas-refresh-stat.removed { background: #f8d7da; color: #721c24; }
        .eas-refresh-stat.unchanged { background: #e9ecef; color: #495057; }
        .eas-refresh-stat.error { background: #f8d7da; color: #721c24; }
        .eas-refresh-footer { margin-top: 16px; padding-top: 14px; border-top: 1px solid #e5e5e5; }
        .eas-refresh-total { font-size: 14px; color: #666; }
        .eas-refresh-total strong { color: #00aff0; font-size: 16px; }

        .eas-loading-overlay {
          position: fixed; inset: 0; background: rgba(255,255,255,0.7);
          z-index: 99999; display: flex; justify-content: center; align-items: center;
          flex-direction: column; gap: 16px;
        }
        .eas-loading-spinner {
          width: 48px; height: 48px; border: 4px solid #e5e5e5; border-top-color: #00aff0;
          border-radius: 50%; animation: easSpin 0.7s linear infinite;
        }
        @keyframes easSpin { to { transform: rotate(360deg); } }
        .eas-loading-text { font-size: 15px; font-weight: 600; color: #555; }

        @media (max-width: 1200px) {
          .eas-summary { grid-template-columns: repeat(5, 1fr); }
          .eas-stat-card:nth-child(5n) { border-right: none; }
          .eas-stat-card:nth-child(n+6) { border-top: 1px solid #e5e5e5; }
          .eas-step-detail-grid { grid-auto-flow: row; grid-template-columns: repeat(4, 1fr); }
          .eas-step-detail-stat:nth-child(4n) { border-right: none; }
          .eas-step-detail-stat:nth-child(n+5) { border-top: 1px solid #e5e5e5; }
        }
        @media (max-width: 768px) {
          .eas-summary { grid-template-columns: repeat(2, 1fr); }
          /* Undo the 5-column rule first: card 5 is a left-hand card here. */
          .eas-stat-card:nth-child(5n) { border-right: 1px solid #e5e5e5; }
          .eas-stat-card:nth-child(2n) { border-right: none; }
          .eas-stat-card:nth-child(n+3) { border-top: 1px solid #e5e5e5; }
          .eas-details-grid { grid-template-columns: 1fr; }
          .eas-detail-label { border-bottom: none; padding-bottom: 5px; }
          .eas-detail-value { padding-top: 5px; }
          .eas-step-detail-grid { grid-auto-flow: row; grid-template-columns: repeat(2, 1fr); }
          .eas-step-detail-stat:nth-child(2n) { border-right: none; }
          .eas-step-detail-stat:nth-child(n+3) { border-top: 1px solid #e5e5e5; }
        }
      `}</style>

      {loadingOverlay && (
        <div className="eas-loading-overlay">
          <div className="eas-loading-spinner" />
          <div className="eas-loading-text">{loadingOverlay}</div>
        </div>
      )}

      <div className="eas-page">
        {/* Header */}
        <div className="eas-header">
          <div>
            <h1>{automation.name} <span className={`eas-channel-tag eas-channel-${automation.channel}`}>{automation.channel}</span></h1>
            <div className="eas-meta">
              <span className={`eas-badge ${statusClass}`}>
                {automation.status.charAt(0).toUpperCase() + automation.status.slice(1)}
              </span>
              {isActive && (
                <button type="button" className="eas-action-btn" onClick={() => setPauseConfirmOpen(true)} style={{ fontSize: 12, padding: '4px 10px' }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16" rx="1" /><rect x="14" y="4" width="4" height="16" rx="1" /></svg>
                  Pause
                </button>
              )}
              {isPaused && (
                <>
                  <button type="button" className="eas-action-btn primary" onClick={handleResume} style={{ fontSize: 12, padding: '4px 10px' }}>Resume</button>
                  <button type="button" className="eas-action-btn" onClick={() => navigate(`/marketing/automations/email/${automationId}`)} style={{ fontSize: 12, padding: '4px 10px' }}>
                    Edit Automation
                  </button>
                </>
              )}
              <span>Created {formatCreatedDate(automation.createdAt)}</span>
            </div>
          </div>

          <div className="eas-header-right">
            <div className="eas-date-form">
              <label>From:</label>
              <DatePicker size="sm" ariaLabel="From" value={dateFrom} onChange={setDateFrom} wrapperClassName="w-36" />
              <label>To:</label>
              <DatePicker size="sm" ariaLabel="To" value={dateTo} onChange={setDateTo} wrapperClassName="w-36" />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span
                className="eas-contacts-link"
                onClick={() => navigate(`/marketing/automations/email/${automationId}/contacts`)}
                title="View contacts"
              >
                <span style={{ fontSize: 20, fontWeight: 700, color: '#00aff0' }}>{fmtNum(contacts.total)}</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#333' }}>{contacts.total === 1 ? 'contact' : 'contacts'}</span>
              </span>
              <button type="button" className="eas-action-btn" onClick={handleRefresh} disabled={syncMut.isPending} style={{ fontSize: 12, padding: '5px 10px' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={syncMut.isPending ? { animation: 'easSpin 0.7s linear infinite' } : undefined}><path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2" /></svg>
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Held steps: SendGrid refused a step for a reason that is not the
            recipient's. Cleared by the API after the next send on the step
            that goes through, or once nobody is waiting on it. */}
        {heldSteps.length > 0 ? (
          <div role="alert" className="eas-hold-banner">
            <div className="eas-hold-head">
              <div className="eas-hold-title">
                {heldSteps.length === 1
                  ? `Step ${heldSteps[0].stepOrder} is held: SendGrid is refusing its emails`
                  : `Steps ${heldSteps.map((s) => s.stepOrder).join(', ')} are held: SendGrid is refusing their emails`}
              </div>
              <button
                type="button"
                className="eas-action-btn"
                onClick={() => retryHoldMut.mutate()}
                disabled={retryHoldMut.isPending}
                style={{ fontSize: 12, padding: '5px 10px' }}
              >
                Try again now
              </button>
            </div>
            <div className="eas-hold-text">
              SendGrid refused a send for a reason that is not the recipient&apos;s, such as an unverified sender or an
              outage. A held step tries one contact an hour until a send goes through, and the other steps keep
              sending. Held tries do not count against contacts; each shows in that contact&apos;s send history.
            </div>
            <div className="eas-hold-text" style={{ marginTop: 6 }}>
              <strong>Who fixes it:</strong> usually whoever manages your SendGrid account, for example by verifying
              the step&apos;s sender address in SendGrid, or by changing the step&apos;s sender to one that is
              verified. Once it is fixed, Try again now makes the next try within 15 minutes.
            </div>
            {heldSteps.map((s) => (
              <div key={s.stepId} className="eas-hold-step">
                <div className="eas-hold-message">
                  <span className="eas-hold-message-label">Step {s.stepOrder} · SendGrid&apos;s message</span>
                  {s.reason}
                </div>
                <div className="eas-hold-times">
                  <span>Held since <strong>{formatPacificDateTime(s.since, userTz)}</strong></span>
                  <span>Last try <strong>{formatPacificDateTime(s.lastTry, userTz)}</strong></span>
                  <span>
                    Next try{' '}
                    <strong>{s.nextTry ? formatPacificDateTime(s.nextTry, userTz) : 'within 15 minutes'}</strong>
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : null}

        {/* Stats Summary */}
        <div className="eas-summary">
          {STAT_CARDS.map(card => {
            const val = cardValues[card.key] ?? 0;
            const isZero = val === 0;
            const color = isZero ? undefined : card.color;
            const box = statBox(isZero ? null : () => openContacts(card.link));
            return (
              <div key={card.key} className={cn('eas-stat-card', box.className)} {...box.props}>
                <div className={`eas-stat-value${isZero ? ' zero' : ''}`} style={color ? { color } : undefined}>
                  {fmtNum(val)}
                </div>
                <div className="eas-stat-label">{card.label}</div>
              </div>
            );
          })}
        </div>

        {/* Chart */}
        <div className="eas-chart-container">
          {dailyStats.length > 0 ? (
            <CampaignDailyChart data={dailyStats} storageKey="marketing.emailAutomationStats.chartType" />
          ) : (
            <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999', fontSize: 14 }}>
              No daily data available for the selected date range.
            </div>
          )}
        </div>

        {/* Per-Step Analytics */}
        <div className="eas-section">
          <div className="eas-section-header">
            Step Performance
            <span className="eas-info-icon" title="Analytics for each step in this automation">i</span>
          </div>

          {stepsData.length > 0 ? (
            <div style={{ padding: '16px 20px' }}>
              {stepsData.map((step, idx) => renderStepCard(step, idx === 0))}
            </div>
          ) : (
            <div className="eas-empty-state">
              <div style={{ fontSize: 32, marginBottom: 10 }}>📧</div>
              <p>No steps configured for this automation.</p>
            </div>
          )}

          <RemovedStepsSection count={removedSteps.length} noun="emails">
            {removedSteps.map((step) => renderStepCard(step, step.previousStepOrder === 1, step))}
          </RemovedStepsSection>
        </div>

        {/* Click Tracking */}
        <div className="eas-section">
          <div className="eas-section-header">
            Click Tracking
            <span className="eas-info-icon" title="Shows which links were clicked across all steps">i</span>
          </div>

          {clickTracking.length > 0 ? (
            <>
              <table className="eas-table">
                <thead>
                  <tr>
                    <th style={{ width: 60 }}>Rank</th>
                    <th>Link (URL)</th>
                    <th style={{ width: 120 }}>Total Clicks</th>
                  </tr>
                </thead>
                <tbody>
                  {clickTracking.map((link, idx) => {
                    const rank = idx + 1;
                    const rankClass = rank === 1 ? 'eas-rank-1' : rank === 2 ? 'eas-rank-2' : rank === 3 ? 'eas-rank-3' : 'eas-rank-default';
                    return (
                      <tr key={idx}>
                        <td><span className={`eas-rank-badge ${rankClass}`}>{rank}</span></td>
                        <td><a href={link.url} target="_blank" rel="noopener noreferrer" className="eas-link-url">{link.url}</a></td>
                        <td>{fmtNum(link.clicks)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="eas-table-footer">
                <span>Displaying {clickTracking.length} links</span>
                <span>Total Clicks: <strong>{fmtNum(totalClicks)}</strong></span>
              </div>
            </>
          ) : (
            <div className="eas-empty-state">
              <div style={{ fontSize: 32, marginBottom: 10 }}>📊</div>
              <p>No click tracking data available yet.</p>
            </div>
          )}
        </div>

        {/* Details */}
        <div className="eas-section">
          <div className="eas-section-header">Details</div>
          <div className="eas-details-grid">
            <div className="eas-detail-row" style={{ display: 'contents' }}>
              <div className="eas-detail-label">Automation Name</div>
              <div className="eas-detail-value">{automation.name}</div>
            </div>
            <div className="eas-detail-row" style={{ display: 'contents' }}>
              <div className="eas-detail-label">Status</div>
              <div className="eas-detail-value">
                <span className={`eas-badge ${statusClass}`}>{automation.status.charAt(0).toUpperCase() + automation.status.slice(1)}</span>
              </div>
            </div>
            <div className="eas-detail-row" style={{ display: 'contents' }}>
              <div className="eas-detail-label">Entry List</div>
              <div className="eas-detail-value">
                {automation.entryListId ? (
                  <Link to={`/marketing/contact-lists/${automation.entryListId}/details`} title="Open in Contact Lists">
                    {automation.entryListName || `List #${automation.entryListId}`}
                  </Link>
                ) : (automation.entryListName || '—')}
              </div>
            </div>
            <div className="eas-detail-row" style={{ display: 'contents' }}>
              <div className="eas-detail-label">Steps</div>
              <div className="eas-detail-value">{stepsData.length} email step{stepsData.length !== 1 ? 's' : ''}</div>
            </div>
            <div className="eas-detail-row" style={{ display: 'contents' }}>
              <div className="eas-detail-label">Exit Criteria</div>
              <div className="eas-detail-value">{EXIT_CRITERIA_LABELS[automation.exitCriteria ?? 0] ?? 'Unknown'}</div>
            </div>
            <div className="eas-detail-row" style={{ display: 'contents' }}>
              <div className="eas-detail-label">Allow Re-entry</div>
              <div className="eas-detail-value">{automation.allowReentry ? 'Yes — contacts who leave and return will restart the automation' : 'No — contacts can only enter once'}</div>
            </div>
            <div className="eas-detail-row" style={{ display: 'contents' }}>
              <div className="eas-detail-label">Contacts</div>
              <div className="eas-detail-value">
                <Link to={`/marketing/automations/email/${automationId}/contacts`} className="eas-contacts-link" style={{ padding: '4px 8px' }}>
                  <span style={{ fontSize: 18, fontWeight: 600, color: '#00aff0' }}>{fmtNum(contacts.total)}</span>
                  <span>{contacts.total === 1 ? 'contact' : 'contacts'}</span>
                </Link>
                <span style={{ marginLeft: 12, color: '#999', fontSize: 12 }}>
                  ({fmtNum(contacts.active)} active, {fmtNum(contacts.completed)} completed, {fmtNum(contacts.removed)} removed,
                  {' '}{fmtNum(contacts.invalid)} invalid, {fmtNum(contacts.failed)} stopped after retries)
                </span>
              </div>
            </div>
            {automation.unsubscribeGroup && (
              <div className="eas-detail-row" style={{ display: 'contents' }}>
                <div className="eas-detail-label">Unsubscribe Group</div>
                <div className="eas-detail-value">{automation.unsubscribeGroup}</div>
              </div>
            )}
            {automation.categories && (
              <div className="eas-detail-row" style={{ display: 'contents' }}>
                <div className="eas-detail-label">Categories</div>
                <div className="eas-detail-value">{automation.categories}</div>
              </div>
            )}
          </div>
        </div>

        {/* History */}
        <MarketingHistoryCard type="email_automation" id={automationId} className="mb-[25px]" />
      </div>

      <PauseAutomationDialog
        isOpen={pauseConfirmOpen}
        channel="email"
        automationName={automation.name}
        activeContacts={contacts.active}
        onClose={() => setPauseConfirmOpen(false)}
        onConfirm={confirmPause}
      />

      {/* Content Modal */}
      {contentModalOpen && contentModalStep && (
        <div className="eas-modal-overlay" onClick={e => { if (e.target === e.currentTarget) setContentModalOpen(false); }}>
          <div className="eas-modal">
            <div className="eas-modal-header">
              <h3>{contentModalStep.subject}</h3>
              <button className="eas-modal-close" onClick={() => setContentModalOpen(false)}>&times;</button>
            </div>
            <div className="eas-modal-body">
              <iframe srcDoc={contentModalStep.html} sandbox="allow-same-origin" />
            </div>
          </div>
        </div>
      )}

      {/* Refresh Modal */}
      {refreshModalOpen && (
        <div className="eas-refresh-overlay" onClick={e => { if (e.target === e.currentTarget) setRefreshModalOpen(false); }}>
          <div className="eas-refresh-modal">
            <div className="eas-refresh-header">
              <h3>Refresh Results</h3>
              <button className="eas-modal-close" onClick={() => setRefreshModalOpen(false)}>&times;</button>
            </div>
            <div className="eas-refresh-body">
              {refreshResult?.error ? (
                <>
                  <div className="eas-refresh-icon">⚠️</div>
                  <div className="eas-refresh-message">Refresh Failed</div>
                  <div className="eas-refresh-details"><span className="eas-refresh-stat error">{refreshResult.error}</span></div>
                </>
              ) : (refreshResult?.added ?? 0) > 0 || (refreshResult?.removed ?? 0) > 0 ? (
                <>
                  <div className="eas-refresh-icon">✅</div>
                  <div className="eas-refresh-message">Contacts Updated</div>
                  <div className="eas-refresh-details" style={{ marginTop: 12 }}>
                    {(refreshResult?.added ?? 0) > 0 && <span className="eas-refresh-stat added">+ {refreshResult!.added} added</span>}
                    {(refreshResult?.removed ?? 0) > 0 && <span className="eas-refresh-stat removed">{refreshResult!.removed} removed</span>}
                  </div>
                  <div className="eas-refresh-footer">
                    <span className="eas-refresh-total">Total contacts: <strong>{fmtNum(refreshResult?.total_contacts ?? contacts.total)}</strong></span>
                  </div>
                </>
              ) : (
                <>
                  <div className="eas-refresh-icon">👍</div>
                  <div className="eas-refresh-message">No Changes</div>
                  <div className="eas-refresh-details"><span className="eas-refresh-stat unchanged">Contact list is already up to date</span></div>
                  <div className="eas-refresh-footer">
                    <span className="eas-refresh-total">Total contacts: <strong>{fmtNum(refreshResult?.total_contacts ?? contacts.total)}</strong></span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
